import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';

function tryParseInt(str: string): number | undefined {
    const val: number = Number.parseInt(str);
    if (isNaN(val) || val < 0) {
        return undefined;
    }
    return val;
}

@Component({
    selector: 'app-input-otp',
    templateUrl: './input-otp.component.html',
    styleUrls: ['./input-otp.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: InputOtpComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: InputOtpComponent,
        },
    ],
})
export class InputOtpComponent implements ControlValueAccessor, Validator, OnInit {
    @Input({ required: true }) numberOfDigits: number;
    @Input() class: string;
    @Output() onChange: EventEmitter<string | null> = new EventEmitter();
    dummyList: unknown[];
    numbers: number[];
    disabled: boolean = false;
    oldValue: string | null;

    private touched: boolean = false;
    private onTouched: () => void;
    private onFormChange: (_: string | null) => void = (v) => this.onChange.emit(v);

    private setInputValue(index: number, event: Event): boolean {
        const ipt = event.currentTarget as HTMLInputElement;
        const valStr = ipt.value;
        const val = tryParseInt(valStr);
        if (val === undefined) {
            ipt.value = '';
            return false;
        }
        this.numbers[index] = val;
        return true;
    }

    cancelArrowInput(event: KeyboardEvent) {
        const ipt = event.currentTarget as HTMLInputElement;
        if (event.code === 'ArrowRight' || event.code === 'ArrowLeft') event.preventDefault();
    }

    onDigitInput(index: number, event: KeyboardEvent) {
        const ipt = event.currentTarget as HTMLInputElement;
        let element;
        if (event.code.includes('Numpad') || event.code.includes('Digit')) {
            if (!this.setInputValue(index, event)) return;
            element = ipt.nextElementSibling;
        }
        if (event.code === 'Backspace') {
            delete this.numbers[index];
            element = ipt.previousElementSibling;
        }
        if (event.code === 'ArrowRight') element = ipt.nextElementSibling;
        if (event.code === 'ArrowLeft') element = ipt.previousElementSibling;
        // this.markAsTouched();
        this.handleCompletion();
        if (element == null) return;
        element.focus();
    }

    private handleCompletion() {
        const str = this.numbers.join('');
        if (str.length != this.numberOfDigits) {
            if (this.oldValue === undefined) return;
            this.oldValue = null;
            this.onFormChange(null);
            return;
        }
        if (this.oldValue !== str) {
            this.oldValue = str;
            this.onFormChange(str);
        }
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    ngOnInit(): void {
        if (this.numberOfDigits <= 0) throw new Error('Cannot have number of digits less or equal to 0');
        this.dummyList = Array(this.numberOfDigits);
        this.numbers = Array(this.numberOfDigits);
    }

    validate(control: AbstractControl<any, any>): ValidationErrors | null {
        const str = this.numbers.join('');
        if (0 < str.length && str.length < this.numberOfDigits) return { incomplete: true };
        return null;
    }

    writeValue(obj: any): void {
        if (obj === null) return;
        if (typeof obj === 'string') {
            if (obj.length !== this.numberOfDigits) throw new Error('Invalid argument [NOT_CORRECT_LENGTH]');
            const splitted = obj.split('');
            for (const i in splitted) {
                const digitStr = splitted[i];
                const val = tryParseInt(digitStr);
                if (val === undefined) throw new Error('Invalid argument [NOT_A_NUMBER]');
                this.numbers[i] = val;
            }
            return;
        } else if (Array.isArray(obj)) {
            if (obj.length !== this.numberOfDigits) throw new Error('Invalid argument [NOT_CORRECT_LENGTH]');
            for (const i in obj) {
                const el = obj[i];
                if (typeof el !== 'number') throw new Error('Invalid argument [NOT_A_NUMBER]');
                if (el < 0 || Math.floor(el) !== el) throw new Error('Invalid argument [NOT_A_POSITIVE_INTEGER]');
                if (el > 9) throw new Error('Invalid argument [NOT_A_DIGIT]');
                this.numbers[i] = el;
            }
            return;
        }
        throw new Error('Invalid argument [WRONG_TYPE]');
    }

    handlePaste(event: ClipboardEvent) {
        event.preventDefault();

        let paste = event.clipboardData?.getData('text');
        if (paste === undefined || paste.length !== this.numberOfDigits) return;
        const digitsStr = paste.split('');
        const tempDigits = Array(this.numberOfDigits);
        for (const i in digitsStr) {
            const str = digitsStr[i];
            const num = tryParseInt(str);
            if (num === undefined) return;
            tempDigits[i] = num;
        }

        for (const i in tempDigits) {
            this.numbers[i] = tempDigits[i];
        }

        this.dummyList = this.dummyList;

        let el = event.currentTarget as HTMLInputElement;
        while (el.nextElementSibling !== null) el = el.nextElementSibling as HTMLInputElement;
        el.focus();
    }

    registerOnChange(fn: (_: string | null) => void): void {
        this.onFormChange = (v) => {
            fn(v);
            this.onChange.emit(v);
        };
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
